import { isApiError } from '@saay/api-client';
import { Money } from '@saay/blocks';
import {
  formatCount,
  formatLocalDate,
  formatTimestamp,
  formatToman,
  formatWeekdayDate,
  type LocalDate,
  statusLabel,
  statusValues,
} from '@saay/formatting';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';

import type {
  CashSettlementBatch,
  CashSettlementBatchItem,
  SettlementBatchExecution,
  SettlementBatchExecutionCreate,
} from '../api.js';
import { AdminListTable, type DataTableColumn } from '../components/admin-list-table.js';
import { DateFilterPill, FilterPill, FilterPillBar } from '../components/filter-pills.js';
import { QueryState, usePageTitle } from '../components/guard.js';
import { ReasonDialog } from '../components/reason-dialog.js';
import { onSheetOpenChange, OPENED_FROM_LIST, useCloseSheet } from '../components/route-sheet.js';
import {
  Banner,
  Button,
  Dialog,
  EmptyState,
  FactList,
  LoadMore,
  Ltr,
  Masked,
  Section,
  Skeleton,
  StatusChip,
} from '../kit.js';
import { canManageSettlements } from '../permissions.js';
import {
  listStatus,
  setupKeys,
  useAdminSelf,
  useCashSettlementBatch,
  useCashSettlementBatches,
} from '../queries.js';

/**
 * Daily cash settlement (D-122, AC §34.7.3): the batch list (CS3), the batch sheet (CS4), and the
 * execution (CS5). A cash batch is a date, not a week: no calendar, no week fields, no commission.
 */
export const BATCH_KIND_OPTIONS = statusValues('settlementBatchKind').map((value) => ({
  label: statusLabel('settlementBatchKind', value),
  value,
}));

const BATCH_STATUS_OPTIONS = statusValues('settlementBatchStatus').map((value) => ({
  label: statusLabel('settlementBatchStatus', value),
  value,
}));

function dayText(value: string): string {
  return formatWeekdayDate(value as LocalDate, { withYear: true });
}

function cashBatchPath(id: string): string {
  return `/settlements/cash/${encodeURIComponent(id)}`;
}

/** The «دسته‌های نقدی روزانه» tab of `/settlements/cash` (CS3). */
export function CashBatches() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const from = (params.get('from') ?? '') as LocalDate;
  const to = (params.get('to') ?? '') as LocalDate;
  const list = useCashSettlementBatches({
    settlementDateFrom: from,
    settlementDateTo: to,
    status,
  });
  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];
  const activeFilters = (status === '' ? 0 : 1) + (from === '' && to === '' ? 0 : 1);

  function patch(changes: Readonly<Record<string, string>>): void {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value === '') next.delete(key);
      else next.set(key, value);
    }
    setParams(next);
  }

  const columns: readonly DataTableColumn<CashSettlementBatchItem>[] = [
    {
      header: 'فروش‌های روز',
      id: 'day',
      render: (row) => (
        <Link state={OPENED_FROM_LIST} to={cashBatchPath(row.id)}>
          {dayText(row.settlementDate)}
        </Link>
      ),
    },
    { header: 'واریز', id: 'payout', render: (row) => dayText(row.scheduledPayoutDate) },
    { header: 'فروش', id: 'sales', render: (row) => formatCount(row.saleCount) },
    { header: 'فروشگاه', id: 'merchants', render: (row) => formatCount(row.merchantCount) },
    { header: 'ناخالص', id: 'gross', render: (row) => <Money rials={row.grossIrr} /> },
    { header: 'کارمزد', id: 'fee', render: (row) => <Money rials={row.saayFeeIrr} /> },
    { header: 'خالص', id: 'net', render: (row) => <Money rials={row.netIrr} /> },
    {
      header: 'وضعیت',
      id: 'status',
      render: (row) => <StatusChip enumName="settlementBatchStatus" value={row.status} />,
    },
  ];

  return (
    <Section id="cash-settlement-batches" title="دسته‌های نقدی روزانه">
      <p className="mb-3 text-sm text-muted-foreground">
        فروش نقدی هر روز، روز بعد خودکار واریز می‌شود؛ جمعه و تعطیل هم. اجرای دستی فقط برای وقتی است
        که واریز خودکار انجام نشده باشد.
      </p>
      <FilterPillBar
        activeFilterCount={activeFilters}
        label="فیلتر دسته‌های نقدی"
        onClearAll={() => patch({ from: '', status: '', to: '' })}
      >
        <FilterPill
          label="وضعیت"
          onChange={(value) => patch({ status: value })}
          options={BATCH_STATUS_OPTIONS}
          value={status}
        />
        <DateFilterPill
          from={from}
          namePrefix="cash-settlement-date"
          onApply={(nextFrom, nextTo) => patch({ from: nextFrom, to: nextTo })}
          to={to}
        />
      </FilterPillBar>
      <QueryState
        empty={
          <EmptyState
            action={
              activeFilters === 0 ? undefined : (
                <Button onClick={() => patch({ from: '', status: '', to: '' })} variant="secondary">
                  پاک‌کردن فیلتر
                </Button>
              )
            }
            title={
              activeFilters === 0
                ? 'هنوز دستهٔ نقدی‌ای ساخته نشده است'
                : 'دسته‌ای با این فیلتر پیدا نشد'
            }
          />
        }
        error={list.error}
        isEmpty={rows.length === 0}
        onRetry={() => void list.refetch()}
        skeleton={<Skeleton lines={5} />}
        status={listStatus(list)}
      >
        <AdminListTable
          caption="فهرست دسته‌های نقدی روزانه"
          columns={columns}
          mobileSummary={(row) => ({
            primary: dayText(row.settlementDate),
            secondary: (
              <span className="inline-flex flex-wrap items-center gap-2">
                {formatToman(row.netIrr)}
                <StatusChip enumName="settlementBatchStatus" value={row.status} />
              </span>
            ),
          })}
          onRowClick={(row) => void navigate(cashBatchPath(row.id), { state: OPENED_FROM_LIST })}
          regionLabel="جدول دسته‌های نقدی روزانه"
          rowKey={(row) => row.id}
          rows={rows}
        />
        <LoadMore
          hasMore={list.hasNextPage}
          loading={list.isFetchingNextPage}
          onLoadMore={() => void list.fetchNextPage()}
        />
      </QueryState>
    </Section>
  );
}

/** `/settlements/cash/:batchId` (CS4): the batch sheet. A weekly batch id answers `404`. */
export function CashSettlementBatchSheet() {
  usePageTitle('تسویهٔ فروشگاه‌ها');
  const { batchId = '' } = useParams();
  const batch = useCashSettlementBatch(batchId);
  const close = useCloseSheet('/settlements/cash');
  const self = useAdminSelf().data;
  return (
    <Dialog
      onOpenChange={onSheetOpenChange(close)}
      open
      size="wide"
      title="جزئیات دستهٔ نقدی روزانه"
    >
      <QueryState
        error={batch.error}
        onRetry={() => void batch.refetch()}
        skeleton={<Skeleton lines={6} />}
        status={batch.status}
      >
        {batch.data === undefined ? null : (
          <CashBatchDetail
            batch={batch.data}
            canExecute={self !== undefined && canManageSettlements(self)}
          />
        )}
      </QueryState>
    </Dialog>
  );
}

function CashAttemptCard({
  attempt,
  index,
}: {
  readonly attempt: CashSettlementBatch['attempts'][number];
  readonly index: number;
}) {
  return (
    <div className="flex flex-col gap-2 rounded-2xl bg-card p-4">
      <div className="flex items-center justify-between gap-2">
        <strong>{`واریز ${formatCount(index + 1)}`}</strong>
        <StatusChip enumName="payoutAttemptStatus" value={attempt.status} />
      </div>
      <FactList
        items={[
          { key: 'amount', label: 'مبلغ واریز', value: formatToman(attempt.payoutAmountIrr) },
          ...(attempt.recoveryOffsetIrr === 0
            ? []
            : [
                {
                  key: 'offset',
                  label: 'کسر بازیافت',
                  value: formatToman(attempt.recoveryOffsetIrr),
                },
              ]),
          {
            key: 'sheba',
            label: 'شبا',
            value:
              attempt.shebaMasked === null ? (
                '—'
              ) : (
                <Masked kind="sheba" value={attempt.shebaMasked} />
              ),
          },
          ...(attempt.bankReference === null
            ? []
            : [
                {
                  key: 'reference',
                  label: 'مرجع بانکی',
                  value: (
                    <span className="break-all">
                      <Ltr>{attempt.bankReference}</Ltr>
                    </span>
                  ),
                },
              ]),
          {
            key: 'merchant',
            label: 'فروشگاه',
            value: (
              <Link to={`/merchants/${encodeURIComponent(attempt.merchantId)}`}>دیدن فروشگاه</Link>
            ),
          },
        ]}
      />
    </div>
  );
}

function CashBatchDetail({
  batch,
  canExecute,
}: {
  readonly batch: CashSettlementBatch;
  readonly canExecute: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="admin-sections">
      <FactList
        items={[
          { key: 'day', label: 'فروش‌های روز', value: dayText(batch.settlementDate) },
          { key: 'payout', label: 'تاریخ واریز', value: dayText(batch.scheduledPayoutDate) },
          { key: 'gross', label: 'ناخالص', value: formatToman(batch.grossIrr) },
          { key: 'fee', label: 'کارمزد SAAY', value: formatToman(batch.saayFeeIrr) },
          { key: 'net', label: 'خالص، پیش از کسر بازیافت', value: formatToman(batch.netIrr) },
          { key: 'sales', label: 'فروش', value: formatCount(batch.saleCount) },
          { key: 'merchants', label: 'فروشگاه', value: formatCount(batch.merchantCount) },
          {
            key: 'finalized',
            label: 'نهایی شدن',
            value: batch.finalizedAt === null ? '—' : formatTimestamp(batch.finalizedAt),
          },
          {
            key: 'status',
            label: 'وضعیت',
            value: <StatusChip enumName="settlementBatchStatus" value={batch.status} />,
          },
        ]}
      />
      {batch.saleCount === 0 && batch.attempts.length > 0 ? (
        <Banner tone="info">
          این روز فروش جدیدی ندارد؛ این دسته فقط فروش‌های جامانده از روزهای قبل را واریز می‌کند.
        </Banner>
      ) : null}
      {canExecute && batch.status === 'FINALIZED' ? (
        <Button onClick={() => setOpen(true)}>اجرای واریز</Button>
      ) : null}
      <Section id="cash-batch-attempts" title="واریز فروشگاه‌ها">
        {batch.attempts.length === 0 ? (
          <EmptyState title="هنوز واریزی برای این دسته ساخته نشده است" />
        ) : (
          <ul className="flex flex-col gap-2">
            {batch.attempts.map((attempt, index) => (
              <li key={attempt.id}>
                <CashAttemptCard attempt={attempt} index={index} />
              </li>
            ))}
          </ul>
        )}
      </Section>
      {open ? <CashExecute batch={batch} onOpenChange={setOpen} /> : null}
    </div>
  );
}

/** CS5: no approval step (AC §34.7.1), so the `202` here is the accepted run, never maker-checker. */
function CashExecute({
  batch,
  onOpenChange,
}: {
  readonly batch: CashSettlementBatch;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: setupKeys.cashSettlementBatches });
    await qc.invalidateQueries({ queryKey: setupKeys.settlementAttempts });
  };
  return (
    <ReasonDialog<SettlementBatchExecution>
      description={`دستهٔ ${formatLocalDate(batch.settlementDate, 'long')} برای فروشگاه‌هایی که واریز نشده‌اند اجرا می‌شود.`}
      guidance={(error) =>
        isApiError(error) && error.code === 'RESOURCE_VERSION_CONFLICT'
          ? 'اطلاعات به‌روز شد. دوباره بررسی کنید و ثبت بزنید.'
          : undefined
      }
      name="settlement.cash-execute"
      onDone={refresh}
      onError={(error) => {
        if (
          isApiError(error) &&
          (error.code === 'RESOURCE_VERSION_CONFLICT' ||
            error.code === 'SETTLEMENT_NOT_EXECUTABLE' ||
            error.code === 'SETTLEMENT_ALREADY_PAID')
        ) {
          return refresh();
        }
        return undefined;
      }}
      onOpenChange={onOpenChange}
      send={(client, reason, intent) =>
        client.post(`/admin/cash-settlement-batches/${encodeURIComponent(batch.id)}/execution`, {
          body: { expectedVersion: batch.version, reason } satisfies SettlementBatchExecutionCreate,
          intent,
        })
      }
      submitLabel="اجرای واریز"
      title="اجرای واریز نقدی"
    />
  );
}
